import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  TranscodingPresetsState,
  transcodingPresetsAdapter,
} from './transcoding-presets.state';
import { TranscodingPreset } from '../../services/api/transcoding-presets';

/**
 * Feature key for transcoding presets
 */
export const transcodingPresetsFeatureKey = 'transcodingPresets';

/**
 * Feature selector for transcoding presets state
 */
export const selectTranscodingPresetsState =
  createFeatureSelector<TranscodingPresetsState>(transcodingPresetsFeatureKey);

/**
 * Get entity selectors from adapter
 * Provides: selectIds, selectEntities, selectAll, selectTotal
 */
const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = transcodingPresetsAdapter.getSelectors(selectTranscodingPresetsState);

// ==================== Entity Selectors ====================

/**
 * Select all transcoding preset IDs
 */
export const selectTranscodingPresetIds = selectIds;

/**
 * Select transcoding presets as entities (dictionary)
 */
export const selectTranscodingPresetEntities = selectEntities;

/**
 * Select all transcoding presets as an array
 */
export const selectAllTranscodingPresets = selectAll;

/**
 * Select the total count of transcoding presets
 */
export const selectTranscodingPresetsTotal = selectTotal;

// ==================== Loading State Selectors ====================

/**
 * Select a loading state for all presets
 */
export const selectTranscodingPresetsLoading = createSelector(
  selectTranscodingPresetsState,
  (state) => state.loading
);

/**
 * Select loading state for a specific preset by ID
 */
export const selectTranscodingPresetLoadingById = (id: string) =>
  createSelector(
    selectTranscodingPresetsState,
    (state) => state.loadingById[id] || false
  );

/**
 * Select creating state
 */
export const selectTranscodingPresetCreating = createSelector(
  selectTranscodingPresetsState,
  (state) => state.creating
);

/**
 * Select an updating state for a specific preset by ID
 */
export const selectTranscodingPresetUpdating = (id: string) =>
  createSelector(
    selectTranscodingPresetsState,
    (state) => state.updating[id] || false
  );

/**
 * Select a deleting state for a specific preset by ID
 */
export const selectTranscodingPresetDeleting = (id: string) =>
  createSelector(
    selectTranscodingPresetsState,
    (state) => state.deleting[id] || false
  );

/**
 * Select if any operation is in progress
 */
export const selectTranscodingPresetsAnyLoading = createSelector(
  selectTranscodingPresetsState,
  (state) => {
    const hasLoadingById = Object.values(state.loadingById).some(Boolean);
    const hasUpdating = Object.values(state.updating).some(Boolean);
    const hasDeleting = Object.values(state.deleting).some(Boolean);

    return state.loading || state.creating || hasLoadingById || hasUpdating || hasDeleting;
  }
);

// ==================== Error State Selectors ====================

/**
 * Select general error
 */
export const selectTranscodingPresetsError = createSelector(
  selectTranscodingPresetsState,
  (state) => state.error
);

/**
 * Select create error
 */
export const selectTranscodingPresetCreateError = createSelector(
  selectTranscodingPresetsState,
  (state) => state.createError
);

/**
 * Select update error for a specific preset by ID
 */
export const selectTranscodingPresetUpdateError = (id: string) =>
  createSelector(
    selectTranscodingPresetsState,
    (state) => state.updateError[id] || null
  );

/**
 * Select delete error for a specific preset by ID
 */
export const selectTranscodingPresetDeleteError = (id: string) =>
  createSelector(
    selectTranscodingPresetsState,
    (state) => state.deleteError[id] || null
  );

/**
 * Select if there are any errors
 */
export const selectTranscodingPresetsHasErrors = createSelector(
  selectTranscodingPresetsState,
  (state) => {
    const hasUpdateError = Object.values(state.updateError).some((error) => error !== null);
    const hasDeleteError = Object.values(state.deleteError).some((error) => error !== null);

    return !!(state.error || state.createError || hasUpdateError || hasDeleteError);
  }
);

// ==================== Metadata Selectors ====================

/**
 * Select if presets have been loaded at least once
 */
export const selectTranscodingPresetsLoaded = createSelector(
  selectTranscodingPresetsState,
  (state) => state.loaded
);

/**
 * Select last updated timestamp
 */
export const selectTranscodingPresetsLastUpdated = createSelector(
  selectTranscodingPresetsState,
  (state) => state.lastUpdated
);

/**
 * Select selected preset ID
 */
export const selectSelectedTranscodingPresetId = createSelector(
  selectTranscodingPresetsState,
  (state) => state.selectedPresetId
);

/**
 * Select the currently selected preset entity
 */
export const selectSelectedTranscodingPreset = createSelector(
  selectTranscodingPresetEntities,
  selectSelectedTranscodingPresetId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : null)
);

// ==================== Specific Entity Selectors ====================

/**
 * Select a specific preset by ID
 */
export const selectTranscodingPresetById = (id: string) =>
  createSelector(
    selectTranscodingPresetEntities,
    (entities) => entities[id] || null
  );

/**
 * Select presets sorted by name alphabetically
 */
export const selectTranscodingPresetsSortedByName = createSelector(
  selectAllTranscodingPresets,
  (presets) => [...presets].sort((a, b) => a.name.localeCompare(b.name))
);

/**
 * Select presets sorted by creation date (newest first)
 */
export const selectTranscodingPresetsSortedByNewest = createSelector(
  selectAllTranscodingPresets,
  (presets) => [...presets].sort((a, b) => b.createdAt - a.createdAt)
);

/**
 * Select presets sorted by last update (most recently updated first)
 */
export const selectTranscodingPresetsSortedByLastUpdated = createSelector(
  selectAllTranscodingPresets,
  (presets) => [...presets].sort((a, b) => b.updatedAt - a.updatedAt)
);

/**
 * Select presets filtered by codec type
 */
export const selectTranscodingPresetsByCodec = (codec: 'h264' | 'h265') =>
  createSelector(
    selectAllTranscodingPresets,
    (presets) => presets.filter((preset) => preset.videoCodec === codec)
  );

/**
 * Select presets filtered by name search term
 */
export const selectTranscodingPresetsBySearchTerm = (searchTerm: string) =>
  createSelector(
    selectAllTranscodingPresets,
    (presets) => {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      if (!lowerSearchTerm) {
        return presets;
      }
      return presets.filter((preset) =>
        preset.name.toLowerCase().includes(lowerSearchTerm)
      );
    }
  );

// ==================== Composite/Computed Selectors ====================

/**
 * Select a preset with its loading and error states
 */
export const selectTranscodingPresetWithStatus = (id: string) =>
  createSelector(
    selectTranscodingPresetById(id),
    selectTranscodingPresetLoadingById(id),
    selectTranscodingPresetUpdating(id),
    selectTranscodingPresetDeleting(id),
    selectTranscodingPresetUpdateError(id),
    selectTranscodingPresetDeleteError(id),
    (preset, loading, updating, deleting, updateError, deleteError) => ({
      preset,
      loading,
      updating,
      deleting,
      updateError,
      deleteError,
      isProcessing: loading || updating || deleting,
      hasError: !!(updateError || deleteError),
    })
  );

/**
 * Select preset statistics
 */
export const selectTranscodingPresetsStatistics = createSelector(
  selectAllTranscodingPresets,
  (presets: TranscodingPreset[]) => {
    const total: number = presets.length;
    const h264Count: number = presets.filter((preset: TranscodingPreset): boolean => preset.videoCodec === 'h264').length;
    const h265Count: number = presets.filter((preset: TranscodingPreset): boolean => preset.videoCodec === 'h265').length;
    const avgVideoBitrate: number = total > 0
      ? presets.reduce((sum: number, preset: TranscodingPreset): number => sum + (preset.videoBitrateKbps || 0), 0) / total
      : 0;
    const roundedAvgVideoBitrate: number = Math.round(avgVideoBitrate);

    return {
      total: total,
      h264Count: h264Count,
      h265Count: h265Count,
      avgVideoBitrate: roundedAvgVideoBitrate,
    };
  }
);
